import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Inbox, CalendarDays, HandCoins, ArrowUpRight } from "lucide-react";

const LEAVE_PILL = {
    pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
    hr_verified: "bg-indigo-50 text-indigo-700 border-indigo-200",
};
const LOAN_PILL = {
    pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
    accountant_verified: "bg-sky-50 text-sky-700 border-sky-200",
    dept_approved: "bg-violet-50 text-violet-700 border-violet-200",
    finance_approved: "bg-blue-50 text-blue-700 border-blue-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
};

function fmt(n) {
    if (!n) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

export default function MyRequests() {
    const { user } = useAuth();
    const [leaves, setLeaves] = useState([]);
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user?.employee_id) return;
        (async () => {
            try {
                const [lv, ln] = await Promise.all([
                    api.get("/leaves", { params: { employee_id: user.employee_id } }).catch(() => ({ data: [] })),
                    api.get(`/employees/${user.employee_id}/loan-ledger`).catch(() => ({ data: { loans: [] } })),
                ]);
                const OPEN_LEAVE = new Set(["pending", "hr_verified"]);
                const OPEN_LOAN = new Set(["pending", "accountant_verified", "dept_approved", "finance_approved", "approved"]);
                setLeaves((lv.data || []).filter((l) => OPEN_LEAVE.has(l.status)));
                setLoans((ln.data?.loans || []).filter((l) => OPEN_LOAN.has(l.status)));
            } finally {
                setLoading(false);
            }
        })();
    }, [user?.employee_id]);

    const totalOpen = leaves.length + loans.length;

    return (
        <div className="px-5 sm:px-8 md:px-12 py-8 sm:py-12 md:py-16" data-testid="my-requests-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Self-service</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02] flex items-center gap-3">
                        <Inbox className="w-9 h-9 text-klein" strokeWidth={1.5} /> My Requests
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        Everything you've submitted that's still moving through the approval chain. {totalOpen} open request{totalOpen === 1 ? "" : "s"}.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/leaves" className="inline-flex items-center gap-2 border border-klein text-klein px-4 py-2.5 text-sm hover:bg-klein hover:text-white" data-testid="my-requests-new-leave">
                        <CalendarDays className="w-4 h-4" /> New leave
                    </Link>
                    <Link to="/loans" className="inline-flex items-center gap-2 bg-klein text-white px-4 py-2.5 text-sm hover:bg-klein/90" data-testid="my-requests-new-loan">
                        <HandCoins className="w-4 h-4" /> New advance
                    </Link>
                </div>
            </div>

            {loading && <div className="mt-10 text-sm text-ink/40">Loading…</div>}

            {!loading && (
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
                    <div className="bg-white p-6" data-testid="my-requests-leaves">
                        <div className="flex items-end justify-between mb-4">
                            <div>
                                <div className="overline">Leaves awaiting decision</div>
                                <h2 className="mt-1 font-display font-bold text-xl tracking-tight text-ink">{leaves.length} pending</h2>
                            </div>
                            <Link to="/leaves" className="text-xs uppercase tracking-[0.15em] text-ink/60 hover:text-klein">
                                All → <ArrowUpRight className="w-3 h-3 inline" />
                            </Link>
                        </div>
                        {leaves.length === 0 ? (
                            <div className="text-sm text-ink/40 py-6 border-t border-ink/5">No leave requests pending.</div>
                        ) : (
                            <ul className="space-y-1">
                                {leaves.map((l) => (
                                    <li key={l.id} className="flex items-center justify-between py-2 border-b border-ink/5 text-sm" data-testid={`my-leave-${l.id}`}>
                                        <div className="min-w-0">
                                            <div className="text-ink truncate">{l.leave_type} · {l.start_date} → {l.end_date}</div>
                                            {l.reason && <div className="text-xs text-ink/50 truncate">{l.reason}</div>}
                                        </div>
                                        <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.12em] ${LEAVE_PILL[l.status] || "bg-ink/5 text-ink/70 border-ink/10"}`}>{(l.status || "").replace(/_/g, " ")}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="bg-white p-6" data-testid="my-requests-loans">
                        <div className="flex items-end justify-between mb-4">
                            <div>
                                <div className="overline">Loans / advances awaiting decision</div>
                                <h2 className="mt-1 font-display font-bold text-xl tracking-tight text-ink">{loans.length} pending</h2>
                            </div>
                            <Link to="/loans" className="text-xs uppercase tracking-[0.15em] text-ink/60 hover:text-klein">
                                All → <ArrowUpRight className="w-3 h-3 inline" />
                            </Link>
                        </div>
                        {loans.length === 0 ? (
                            <div className="text-sm text-ink/40 py-6 border-t border-ink/5">No loan / advance requests pending.</div>
                        ) : (
                            <ul className="space-y-1">
                                {loans.map((l) => (
                                    <li key={l.id} className="flex items-center justify-between py-2 border-b border-ink/5 text-sm" data-testid={`my-loan-${l.id}`}>
                                        <div className="min-w-0">
                                            <div className="text-ink truncate capitalize">{l.type || "loan"} · {fmt(l.amount)}</div>
                                            {l.reason && <div className="text-xs text-ink/50 truncate">{l.reason}</div>}
                                        </div>
                                        <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.12em] ${LOAN_PILL[l.status] || "bg-ink/5 text-ink/70 border-ink/10"}`}>{(l.status || "").replace(/_/g, " ")}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
